import { Section, Sub, Card, Note, Fields, Code } from '../../../components/docs/Blocks';

export default function WorldPicker() {
  return (
    <Section
      id="world-picker"
      title="Choosing a world"
      lead="The picker is the first screen you see and the only one that does not need a world open. It lists every Minecraft Bedrock world on the computer, whichever Minecraft put it there."
    >
      <Sub id="picker-list" title="The world list">
        <Card>
          <p>
            Worlds are grouped by the install they belong to, so a machine with the
            Microsoft Store version, the standalone launcher and Preview installed
            shows three groups rather than one long list. Each group header names the
            install and, where Minecraft keeps worlds per account, the profile.
          </p>
          <Fields
            items={[
              ['Recently opened worlds', 'A band of cards along the top, newest first, each with the world icon and when you last opened it. A card for a world whose folder has since gone is greyed and says so rather than disappearing.'],
              ['Search', 'Filter the list by name as you type. The count under the title tells you how many of the total are showing.'],
              ['Folder sizes', 'Measured in the background and filled in as they land, so a machine with a 3 GB world does not stall the list waiting for it.'],
              ['Health', 'Each world is checked as it is found. Anything that cannot be opened is flagged before you try, and the header counts how many need attention.'],
              ['Browse for level.dat', 'Points Toolchest at a world it did not find — a copy on another drive, an extracted .mcworld, a server world. Folders you add this way are remembered.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="picker-detail" title="The details pane">
        <Card>
          <p>
            Selecting a world fills the pane on the right: its icon as Minecraft shows
            it, the game mode, difficulty, the Minecraft version it was last opened
            with, the seed, whether experiments are on, its size on disk, when it was
            last played and the folder it lives in.
          </p>
          <p>
            Selecting is not opening. Nothing is read from the world database and no
            lock is taken until you press <strong>Open World</strong>, so you can look
            through every world on the machine without touching any of them.
          </p>
        </Card>
      </Sub>

      <Sub id="picker-health" title="Worlds that cannot be opened">
        <Card>
          <p>
            A world that is not in a fit state to open says so in the list instead of
            failing when you click it.
          </p>
          <Fields
            items={[
              ['No world data', 'The folder has a level.dat but no database — usually a partial copy, or a pack folder that is not a world at all.'],
              ['Database needs repair', <>The pointer file <Code>db/CURRENT</Code> is damaged. Minecraft cannot open the world either. The world data itself is usually intact behind it.</>],
              ['level.dat unreadable', 'The settings file is corrupt or truncated.'],
              ['Open somewhere else', 'Minecraft, a server, or another copy of Toolchest is holding the world. Close it there and press Refresh.'],
            ]}
          />
          <Note>
            Toolchest will not open a world another program is using, and says which
            program is holding it. Returning to the Minecraft main menu is enough to
            release a world — you do not have to quit the game.
          </Note>
        </Card>
      </Sub>

      <Sub id="picker-without-world" title="What you can do without opening a world">
        <Card>
          <p>
            Three of the rail entries do not need a world, and stay available on the
            picker: <strong>Backups</strong>, <strong>Settings</strong> and{' '}
            <strong>About</strong>. Backups in particular is deliberately world-free —
            it lists every world it holds backups for, including worlds whose folders
            have since been deleted, and can recreate one from a backup.
          </p>
          <p>
            The rest of the rail is dimmed until a world is open. Once one is, the
            title bar carries its name and the rail’s <strong>All Minecraft Worlds</strong>{' '}
            entry becomes <strong>Close World</strong>.
          </p>
        </Card>
      </Sub>
    </Section>
  );
}
